'use client';

import { ErrorInfo, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import useLocalStorage from '@/hooks/useLocalStorage';
import { useImageBaseUrl } from '@/atoms/processEnvAtom';
import { ProductCardProps } from '@/components/ProductCard/ProductCard';
import RecentlyViewed from '@/components/RecentlyViewed/RecentlyViewed';
import { RECENTLY_ITEM_KEY } from '@/components/RecentlyViewed/RecentlyViewedTracker';
import { RecentlyViewedProductType } from '@/components/RecentlyViewed/recentlyViewedTypes';
import { isAppearanceImage, toAppearanceImageFromAppearance } from '@/utility/appearanceConfig';
import { removeLocalStorageItem } from '@/utility/localStorage';
import { trackError } from '@/utility/raygun';
import { getAssortment } from '@/utility/shopIds';
import { getAssortmentDetailHrefArgs } from '@/utility/urls';

const STORAGE_KEY = `${RECENTLY_ITEM_KEY}-productType`;

type RecentlyViewedProductTypesProps = {
  className?: string;
};

const RecentlyViewedProductTypes = (props: RecentlyViewedProductTypesProps) => {
  const handleError = useCallback((error: unknown, info: ErrorInfo) => {
    removeLocalStorageItem(STORAGE_KEY);
    trackError(error as Error, info);
  }, []);

  return (
    // Avoid total failure of the page if something goes wrong in this component
    <ErrorBoundary onError={handleError} fallback={null}>
      <InternalRecentlyViewedProductTypes {...props} />
    </ErrorBoundary>
  );
};

const InternalRecentlyViewedProductTypes = ({ className }: RecentlyViewedProductTypesProps) => {
  const [recentlyViewedProductTypes, storeRecentlyViewedItems] = useLocalStorage<
    RecentlyViewedProductType[]
  >(STORAGE_KEY, []);
  const clearHistory = useCallback(() => storeRecentlyViewedItems([]), [storeRecentlyViewedItems]);
  const imageBaseUrl = useImageBaseUrl();

  const items = useMemo<ProductCardProps[]>(() => {
    return recentlyViewedProductTypes.map((productType) => ({
      background: 'gray',
      title: productType.name,
      brand: productType.labels?.brand?.[0]?.name,
      productTypeId: productType.id,
      assortment: getAssortment(productType.isEmbroideryOnly),
      price: productType.price.vatIncluded,
      link: {
        href: getAssortmentDetailHrefArgs({
          productTypeId: productType.id,
          productTypeName: productType.name,
        }),
      },
      imageBaseUrl,
      images: [
        {
          type: 'productType',
          alt: productType.name,
          productTypeId: productType.id,
          viewId: productType.defaultValues.defaultView.id,
          appearanceId: productType.defaultValues.defaultAppearance.id,
          title: productType.name,
        },
      ],

      appearances: productType.appearances.map((appearance) =>
        isAppearanceImage(appearance) ? appearance : toAppearanceImageFromAppearance(appearance)
      ),
    }));
  }, [imageBaseUrl, recentlyViewedProductTypes]);

  if (!items.length) {
    return null;
  }

  return <RecentlyViewed className={className} items={items} clearHistory={clearHistory} />;
};

export default RecentlyViewedProductTypes;
